import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';

interface AddNewItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  label: string;
  placeholder: string;
  onAdd: (value: string) => void;
  buttonText?: string;
  cancelText?: string;
}

const AddNewItemDialog: React.FC<AddNewItemDialogProps> = ({
  open,
  onOpenChange,
  title,
  label,
  placeholder,
  onAdd,
  buttonText = "Add",
  cancelText = "Cancel"
}) => {
  const [value, setValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleAdd = async () => {
    if (value.trim()) {
      setIsLoading(true);
      try {
        await onAdd(value.trim());
        setValue('');
        onOpenChange(false);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancel = () => {
    setValue('');
    onOpenChange(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => {
      if (!open) {
        handleCancel();
      } 
    }}
    >
      <DialogContent className="w-[95vw] max-w-md mx-auto sm:w-full">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className="text-[12px] font-medium text-text-grey">{label}</Label>
            <Input
              value={value}
              onChange={(e) => {
                const newValue = e.target.value;
                // Prevent leading spaces
                if (newValue.startsWith(' ')) {
                  setValue(newValue.trimStart());
                } else {
                  setValue(newValue);
                }
              }}
              placeholder={placeholder}
              onKeyPress={handleKeyPress}
              autoFocus
              disabled={isLoading}
              className="h-10 text-[13px]"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={isLoading}
              className="h-9 px-4 border-[#E2E5EA] text-text-grey hover:bg-page-bg"
            >
              {cancelText}
            </Button>
            <Button
              onClick={handleAdd}
              size="sm"
              className="h-9 px-4 bg-hero-red text-white hover:bg-hero-red-60"
              disabled={!value.trim()}
              loading={isLoading}
              loadingText="Loading..."
            >
              {buttonText}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddNewItemDialog; 